// O IO da Central do Perito (000385) — ZERO regra. Lá as telas gravavam direto nas tabelas pela RLS do dono;
// aqui toda escrita passa por uma RPC que confere o dono no banco (a regra do hub), e a leitura é uma só,
// `perito_ler()`, em jsonb, sem o teto de 1.000 linhas do PostgREST.
//
// Toda função de escrita LANÇA quando o banco recusa: quem chama é a fila do store, que avisa e recarrega
// tudo do banco depois de uma falha (o desenho de lá, em `store.ts`).
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "../supabase";
import type { ExtracaoDoDespacho } from "./despacho";
import {
  documentoDaLinha, filtroDaLinha, historicoDaLinha, linhaDeFiltro, linhaDeHistorico, linhaDeHonorario,
  linhaDePericia, linhaDePrazo, linhaDeTarefa, periciaDaLinha, prazoDaLinha, tarefaDaLinha,
} from "./mapa";
import type { Documento, FiltroSalvo, HistoricoEntry, Honorario, Pericia, Prazo, State, Tarefa } from "./tipos";

export const BUCKET = "perito-documentos";

type Resposta = { ok: boolean; motivo?: string; detalhe?: string; caminho?: string; apagou?: number };

async function rpc(nome: string, args: Record<string, unknown>): Promise<Resposta> {
  const { data, error } = await supabase.rpc(nome, args);
  if (error) throw new Error(`${nome}: ${error.message}`);
  const r = data as Resposta | null;
  if (!r?.ok) throw new Error(r?.detalhe ?? `${nome} recusou`);
  return r;
}

export type Leitura = { estado: State; podeEscrever: boolean } | { erro: string };

/** Tudo do aluno, numa ida. */
export async function carregarDados(): Promise<Leitura> {
  const { data, error } = await supabase.rpc("perito_ler");
  if (error) return { erro: "Não foi possível falar com o hub. Confira a conexão e tente de novo." };
  const r = data as Record<string, unknown> & { ok?: boolean; detalhe?: string; pode_escrever?: boolean };
  if (!r?.ok) return { erro: r?.detalhe ?? "Não foi possível ler a Central do Perito." };
  const l = (k: string) => (Array.isArray(r[k]) ? (r[k] as Record<string, unknown>[]) : []);
  return {
    podeEscrever: r.pode_escrever === true,
    estado: {
      pericias: l("pericias").map(periciaDaLinha),
      tarefas: l("tarefas").map(tarefaDaLinha),
      prazos: l("prazos").map(prazoDaLinha),
      historico: l("historico").map(historicoDaLinha),
      filtrosSalvos: l("filtros").map(filtroDaLinha),
      documentos: l("documentos").map(documentoDaLinha),
    },
  };
}

export const gravar = {
  pericia: (p: Pericia) => rpc("perito_salvar_pericia", { p: linhaDePericia(p) }),
  honorario: (periciaId: string, h: Honorario) => rpc("perito_salvar_honorario", { p: linhaDeHonorario(periciaId, h) }),
  tarefa: (t: Tarefa) => rpc("perito_salvar_tarefa", { p: linhaDeTarefa(t) }),
  prazo: (p: Prazo) => rpc("perito_salvar_prazo", { p: linhaDePrazo(p) }),
  historico: (h: HistoricoEntry) => rpc("perito_registrar_historico", { p: linhaDeHistorico(h) }),
  filtro: (f: FiltroSalvo) => rpc("perito_salvar_filtro", { p: linhaDeFiltro(f) }),
  apagar: (tabela: "perito_pericias" | "perito_honorarios" | "perito_tarefas" | "perito_prazos" | "perito_filtros" | "perito_documentos", id: string) =>
    rpc("perito_apagar", { p_tabela: tabela, p_id: id }),
};

/**
 * Envia um documento: a linha primeiro (o banco devolve o CAMINHO, na pasta do dono e da perícia), o arquivo
 * depois. Se o envio falhar, a linha sai — melhor não ter o documento que ter um nome sem arquivo.
 */
export async function enviarDocumento(periciaId: string, arquivo: Blob, nome: string, tipo: string): Promise<Documento> {
  const id = crypto.randomUUID();
  const r = await rpc("perito_registrar_documento", { p: { id, pericia_id: periciaId, nome, tipo, tamanho: arquivo.size } });
  const caminho = r.caminho as string;
  const envio = await supabase.storage.from(BUCKET).upload(caminho, arquivo, { contentType: tipo, upsert: false });
  if (envio.error) {
    await gravar.apagar("perito_documentos", id).catch(() => undefined);
    throw new Error(`O arquivo não subiu: ${envio.error.message}`);
  }
  return { id, pericia_id: periciaId, nome, tipo, tamanho: arquivo.size, caminho, criadoEm: new Date().toISOString() };
}

/**
 * Apaga arquivos do bucket. O `remove()` responde 200 com a lista vazia quando a política não deixa apagar
 * (a cicatriz da 000285), então o que prova é a lista DEVOLVIDA ter os caminhos pedidos.
 */
export async function apagarArquivos(caminhos: string[]): Promise<void> {
  if (caminhos.length === 0) return;
  const { data, error } = await supabase.storage.from(BUCKET).remove(caminhos);
  if (error) throw new Error(`apagar arquivos: ${error.message}`);
  const saiu = (data ?? []).length;
  if (saiu < caminhos.length) throw new Error(`${caminhos.length - saiu} arquivo(s) não saíram do bucket`);
}

export async function apagarDocumento(doc: Documento): Promise<void> {
  await apagarArquivos([doc.caminho]);
  await gravar.apagar("perito_documentos", doc.id);
}

/** Endereço assinado de 5 minutos para abrir ou baixar o arquivo (o bucket é privado). */
export async function enderecoDoDocumento(doc: Documento, baixar: boolean): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(doc.caminho, 300, baixar ? { download: doc.nome } : undefined);
  if (error || !data) throw new Error(`abrir documento: ${error?.message ?? "sem resposta"}`);
  return data.signedUrl;
}

export type LeituraDoDespacho = { ok: true; dados: ExtracaoDoDespacho } | { ok: false; motivo: string; detalhe: string };

/**
 * A leitura automática do despacho (fase C, 000386): o texto colado ou o arquivo vai à Edge Function
 * `ler-despacho`, que pergunta ao banco se a conta usa a Central e chama o modelo. Nada é gravado: o que volta
 * preenche o cadastro, e o aluno confere antes de salvar.
 *
 * A frase de cada recusa vem PRONTA da função (`detalhe`); escrevê-la aqui seria a segunda tabela de frases. O
 * motivo útil das respostas 4xx e 5xx está no CORPO, em `error.context` (a lição de `abrir.ts`). Cancelada pelo
 * aluno, a chamada some: quem chama confere `sinal.aborted` antes de usar o resultado.
 */
export async function lerDespacho(
  entrada: { texto?: string; arquivo?: { nome: string; tipo: string; base64: string } },
  sinal?: AbortSignal,
): Promise<LeituraDoDespacho> {
  const semConexao = { ok: false as const, motivo: "falha", detalhe: "Não foi possível falar com a leitura automática. Confira a conexão e tente de novo." };
  try {
    const { data, error } = await supabase.functions.invoke("ler-despacho", { body: entrada, signal: sinal });
    if (error) {
      if (error instanceof FunctionsHttpError) {
        // 404 é a plataforma dizendo que a função não está publicada (a publicação é ato à parte do front). "Confira
        // a conexão" mandaria o aluno procurar o defeito no lugar errado, e tentar de novo não muda nada.
        if (error.context.status === 404) {
          return { ok: false, motivo: "nao_publicada", detalhe: "A leitura automática ainda não está disponível. Continue pelo cadastro manual." };
        }
        try {
          const corpo = await error.context.json();
          if (typeof corpo?.detalhe === "string") return { ok: false, motivo: String(corpo.motivo ?? "falha"), detalhe: corpo.detalhe };
        } catch {
          // o corpo não era JSON: cai na frase comum
        }
      }
      return semConexao;
    }
    const r = data as { ok?: boolean; dados?: ExtracaoDoDespacho; motivo?: string; detalhe?: string } | null;
    if (r?.ok && r.dados) return { ok: true, dados: r.dados };
    return { ok: false, motivo: r?.motivo ?? "falha", detalhe: r?.detalhe ?? "A leitura não deu certo. Continue pelo cadastro manual." };
  } catch {
    return semConexao;
  }
}
